import { type Analytics, AnalyticsBrowser } from "@segment/analytics-next";

import type { AnalyticsAdapter } from "../types";

const isBrowser = typeof window !== "undefined";

let analyticsInstance: Analytics | null = null;
let initialized = false;
let initPromise: Promise<void> | null = null;
let pendingOperations: Array<(analytics: Analytics) => void> = [];

export class SegmentAdapter implements AnalyticsAdapter {
  async init(): Promise<void> {
    if (!isBrowser || initialized) return;

    // Return existing promise if initialization is already in progress
    if (initPromise) return initPromise;

    const writeKey = import.meta.env.VITE_SEGMENT_WRITE_KEY as string | undefined;
    if (!writeKey) return;

    initPromise = AnalyticsBrowser.load({ writeKey })
      .then(([analytics]) => {
        analyticsInstance = analytics;
        initialized = true;
        const operations = pendingOperations;
        pendingOperations = [];
        for (const operation of operations) {
          operation(analytics);
        }
      })
      .catch(() => {
        console.error("Failed to load Segment analytics");
        analyticsInstance = null;
        initialized = false;
        initPromise = null;
      });

    return initPromise;
  }

  page(input: { name?: string; properties?: Record<string, unknown> }): void {
    if (!isBrowser) return;
    this.runWhenInitialized((analytics) => {
      void analytics.page(input.name, {
        ...(input.properties ?? {}),
        eventVersion: "v1",
      });
    });
  }

  track(input: { event: string; properties?: Record<string, unknown> }): void {
    if (!isBrowser) return;
    this.runWhenInitialized((analytics) => {
      void analytics.track(input.event, {
        ...(input.properties ?? {}),
        eventVersion: "v1",
      });
    });
  }

  identify(input: { userId?: string; traits?: Record<string, unknown> }): void {
    if (!isBrowser || !input.userId) return;
    this.runWhenInitialized((analytics) => {
      void analytics.identify(input.userId, input.traits);
    });
  }

  reset(): void {
    if (!isBrowser) return;
    this.runWhenInitialized((analytics) => {
      analytics.reset();
    });
  }

  private runWhenInitialized(operation: (analytics: Analytics) => void): void {
    if (initialized && analyticsInstance) {
      operation(analyticsInstance);
      return;
    }

    if (!import.meta.env.VITE_SEGMENT_WRITE_KEY) return;
    pendingOperations.push(operation);
    void this.init();
  }
}
